import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from "react-select";
import { getSelectList } from '../../config/utils';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCompanyFilters, getFilterSellers } from '../../store/actions/rateCard';
import { getAllSellersList } from "../../store/actions";
import DatePickerComponent from '../common/DatePickerComponent';
import { getAllCarrierServicesList } from '../../store/actions';
import { getAllConsignmentCarrierType, getAllConsignmentStatus } from '../../store/actions/inventory.action';
import { DEFAULT_CARRIER_CONNECT_FILTERS } from '../../constant';
import _ from 'lodash';

/* istanbul ignore next */
const CarrierConnectFilter = (props) => {
    const { showFilter, handleReset, fetchCarrierConnectList, filterData, setfilterData, handleDateFieldChange } = props;
    const [companyList, setCompanyList] = useState([]);
    const [sellerList, setSellerList] = useState([]);
    const [allSellers, setAllSellers] = useState([]);
    const [carrierList, setCarrierList] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [dateValidationErrors, setDateValidationErrors] = useState({});
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.auth.user);
    const { filterCompanies, filterAllSellers } = useSelector((state) => state.RateCardReducer);
    const carriers = useSelector((state) => state.shipment.carriers);
    const { consignmentAllCarrierType, consignmentAllStatus } = useSelector((state) => state.InventoryManagement);

    // Enhanced date validation function
    // NOSONAR: Cognitive complexity temporarily suppressed for incremental refactor.
    // eslint-disable-next-line sonar/cognitive-complexity
    const validateDateRange = (date, field, currentFilterData = filterData) => { // NOSONAR
        const errors = { ...dateValidationErrors };
        
        // Clear any existing errors for this field
        delete errors[field];
        delete errors[`${field}_range`];
        
        if (!date) {
            setDateValidationErrors(errors);
            return true; // Allow null/empty dates
        }

        const today = new Date();
        today.setHours(23, 59, 59, 999); // Set to end of today for comparison

        // Check if date is in the future
        if (date > today) {
            errors[field] = 'Future dates are not allowed';
            setDateValidationErrors(errors);
            return false;
        }

        // Check date range constraints
        const updatedData = { ...currentFilterData, [field]: date };
        
        if (field === 'toDate' && updatedData.fromDate) {
            const fromDate = new Date(updatedData.fromDate);
            if (date < fromDate) {
                errors[`${field}_range`] = 'To Date cannot be earlier than From Date';
                setDateValidationErrors(errors);
                return false;
            }
        }
        
        if (field === 'fromDate' && updatedData.toDate) {
            const toDate = new Date(updatedData.toDate);
            if (date > toDate) {
                errors[`${field}_range`] = 'From Date cannot be later than To Date';
                setDateValidationErrors(errors);
                return false;
            }
        }

        if (field === 'modifiedToDate' && updatedData.modifiedFromDate) {
            const modifiedFromDate = new Date(updatedData.modifiedFromDate);
            if (date < modifiedFromDate) {
                errors[`${field}_range`] = 'Modified To Date cannot be earlier than Modified From Date';
                setDateValidationErrors(errors);
                return false;
            }
        }
        
        if (field === 'modifiedFromDate' && updatedData.modifiedToDate) {
            const modifiedToDate = new Date(updatedData.modifiedToDate);
            if (date > modifiedToDate) {
                errors[`${field}_range`] = 'Modified From Date cannot be later than Modified To Date';
                setDateValidationErrors(errors);
                return false;
            }
        }

        setDateValidationErrors(errors);
        return true;
    };

    // Enhanced date change handler with validation
    const handleValidatedDateChange = (date, field) => {
        // Always update the field first to provide immediate feedback
        handleDateFieldChange(date, field);
        
        // Then validate after a small delay to ensure state is updated
        setTimeout(() => {
            validateDateRange(date, field, { ...filterData, [field]: date });
        }, 10);
    };

    // Local helper to compute select option background (kept local to avoid cross-file dependency)
    const selectOptionBackground = (state) => {
        // Use explicit if-statements instead of nested ternary to reduce cognitive complexity
        if (state?.isSelected) {
            return '#007bff';
        }
        if (state?.isFocused) {
            return '#f8f9fa';
        }
        return 'white';
    };

    // Check if user is Seller role
    const isSellerRole = userDetails?.Value?.roleName === 'Seller' || userDetails?.Value?.role === 'Seller';

    const handleClearFilters = () => {
        // Clear all filter selections without closing the filter section
        setfilterData({
            ...filterData,
            companyIdList: [],
            sellerIdList: [],
            carrierIdList: [],
            consignmentStatusIdList: [],
            fromDate: null,
            toDate: null,
            modifiedFromDate: null,
            modifiedToDate: null
        });

        // Clear date validation errors
        setDateValidationErrors({});

        // Make API call to GetCarrierConnectListing without any filters
        const emptyFilters = {
            ...DEFAULT_CARRIER_CONNECT_FILTERS,
            pageSize: filterData.pageSize // Override pageSize for this specific call
        };
        fetchCarrierConnectList(emptyFilters);
    };

    const handleApplyFilter = () => {
        // Check if there are any validation errors before applying filters
        if (Object.keys(dateValidationErrors).length > 0) {
            // Show a brief alert to user about validation errors
            const errorMessages = Object.values(dateValidationErrors).join('; ');
            alert(`Please fix the following date validation errors before applying filters:\n${errorMessages}`);
            return;
        }

        // Create UI data for parent component (preserves dropdown selections)
        let uiData = { ...filterData };
        uiData.pageIndex = 1;
        uiData.sortColumn = '';
        uiData.sortOrder = 'DESCENDING';

        // Convert date fields to Date objects for consistency
        if (uiData.fromDate) {
            uiData.fromDate = new Date(uiData.fromDate);
        }
        if (uiData.toDate) {
            uiData.toDate = new Date(uiData.toDate);
        }
        if (uiData.modifiedFromDate) {
            uiData.modifiedFromDate = new Date(uiData.modifiedFromDate);
        }
        if (uiData.modifiedToDate) {
            uiData.modifiedToDate = new Date(uiData.modifiedToDate);
        }

        // Pass UI data to parent - the parent will handle API transformation
        fetchCarrierConnectList(uiData);
    };

    const fetchCarriers = async () => {
        try {
            const res = await getAllConsignmentCarrierType();
            if (res.data.Status === 200) {
                const dataCarriers = res.data.EntityList?.map((item) => {
                    return {
                        ...item,
                        value: item.id,
                        label: item.carrierName,
                    };
                }) || [];
                setCarrierList(dataCarriers);
            }
        } catch (error) {
            setCarrierList([]);
        }
    };

    useEffect(() => {
        if (!consignmentAllCarrierType?.length) {
            fetchCarriers();
        }
    }, []);

    useEffect(() => {
        if (consignmentAllCarrierType?.length) {
            let dataCarriers = consignmentAllCarrierType?.map((item) => {
                return {
                    ...item,
                    value: item.id,
                    label: item.carrierName,
                };
            });
            setCarrierList(dataCarriers);
        }
    }, [consignmentAllCarrierType]);

    useEffect(() => {
        if (consignmentAllStatus?.length) {
            let dataStatuses = consignmentAllStatus?.map((item) => {
                return {
                    ...item,
                    value: item.id,
                    label: item.name,
                };
            });
            setStatusList(dataStatuses);
        }
    }, [consignmentAllStatus]);

    useEffect(() => {
        let companyList = filterCompanies?.Entity?.listCompanyMasterOrdersEntity || [];
        let sellerList = filterAllSellers?.EntityList || [];
        if (companyList.length) {
            setCompanyList(getSelectList(filterCompanies?.Entity?.listCompanyMasterOrdersEntity, 'Id', 'name'));
        }
        if (sellerList.length) {
            setAllSellers(getSelectList(sellerList, 'id', 'sellerName'));
            setSellerList(getSelectList(sellerList, 'id', 'sellerName'));
        }
    }, [filterCompanies, filterAllSellers]);

    const handleMultiSelect = (value, fieldName) => {
        if (fieldName === "companyIdList") {
            let companyData = _.map(value, "Id");
            fetchSellers(companyData);
            let newFilterData = {
                ...filterData,
                [fieldName]: value
            };
            if (filterData?.sellerIdList?.length) {
                const filteredSellers = filterData.sellerIdList.filter(seller =>
                    companyData.includes(parseInt(seller.companyIds)) // NOSONAR
                );
                newFilterData.sellerIdList = filteredSellers;
            }
            setfilterData(newFilterData);
        } else {
            // Ensure value is always an array (avoid nested ternary for clarity)
            let safeValue;
            if (Array.isArray(value)) {
                safeValue = value;
            } else if (value) {
                safeValue = [value];
            } else {
                safeValue = [];
            }

            setfilterData({
                ...filterData,
                [fieldName]: safeValue
            });
        }
    }

    const fetchSellers = async (companyList) => {
        if (companyList.length === 0) {
            setSellerList(allSellers);
            return;
        }
        let data = {
            companyIds: companyList,
        };
        try {
            let res = await getAllSellersList(data);
            if (res?.data?.Status === 200) {
                let dataList = res?.data?.Entity?.listSellerMasterOrdersEntity.map(
                    (item) => {
                        return {
                            ...item,
                            label: item.sellerAndCompany,
                            value: item.id,
                        };
                    }
                );
                setSellerList(dataList);
            }
        } catch (err) {
            // Error handling for seller list fetch
        }
    };

    useEffect(() => {
        let data = {
            pageIndex: 1,
            pageSize: 10,
            sortColumn: 'name',
            sortOrder: 'ascending'
        };
        let sellerData = {
            "pageIndex": 1,
            "pageSize": 10,
            "sortColumn": "name",
            "sortOrder": "ASCENDING",
            "companyIdList": [0],
            screenType: 2,
        };
        dispatch(fetchCompanyFilters(data));
        dispatch(getFilterSellers(sellerData));
        if (!carriers?.length) {
            dispatch(getAllCarrierServicesList());
        }
        if (!consignmentAllStatus?.length) {
            dispatch(getAllConsignmentStatus());
        }
    }, []);

    return (
        <div className={showFilter ? 'filterContainer filterContainerZIndex show historyFilter zIndex2' : 'filterContainer historyFilter'}
             style={{
                 background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                 border: '1px solid #dee2e6',
                 borderRadius: '8px',
                 padding: '12px',
                 marginBottom: '15px',
                 boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                 position: 'relative',
                 overflow: 'visible',
                 zIndex: 10
             }}>
            {/* Decorative element */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #007bff 0%, #28a745 50%, #ffc107 100%)'
            }}></div>

            {/* Filter Header */}
            <div style={{
                marginBottom: '12px',
                paddingBottom: '8px',
                borderBottom: '1px solid #e9ecef',
                position: 'relative'
            }}>
                <h6 style={{
                    margin: 0,
                    color: '#495057',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px'
                }}>
                <i className="fa fa-filter" style={{
                    marginRight: '6px',
                    color: '#007bff',
                    fontSize: '12px'
                }}></i> Filter Options
                </h6>
                {/* Close Icon */}
                <button
                    onClick={() => {
                        // Clear filters and close the filter section
                        handleReset(null, 'clear');
                    }}
                    style={{
                        position: 'absolute',
                        top: '0',
                        right: '0',
                        background: 'none',
                        border: 'none',
                        color: '#6c757d',
                        fontSize: '16px',
                        cursor: 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#f8f9fa';
                        e.target.style.color = '#dc3545';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#6c757d';
                    }}
                    title="Close Filter"
                >
                    <i className="fa fa-times"></i>
                </button>
            </div>

            <div className='row' style={{ gap: '12px 0', padding: '0 12px' }}>
                {/* Company filter - only for non-Seller roles */}
                {!isSellerRole && (
                    <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                        <div className='row'>
                            <div className='col-12'>
                                <label className='labelHead' style={{
                                    fontWeight: '600',
                                    color: '#495057',
                                    marginBottom: '3px',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }} htmlFor="companyIdList">
                                    <i className="fa fa-building" style={{
                                        marginRight: '3px',
                                        color: '#6c757d',
                                        fontSize: '10px'
                                    }}></i> Company
                                </label>
                            </div>
                            <div className='col-12 fontFix'>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={companyList}
                                    classNamePrefix="list"
                                    id='companyIdList'
                                    name="companyIdList"
                                    value={filterData?.companyIdList}
                                    onChange={(value) =>
                                        handleMultiSelect(value, "companyIdList")
                                    }
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            borderRadius: '4px',
                                            border: '1px solid #ced4da',
                                            boxShadow: 'none',
                                            minHeight: '28px',
                                            fontSize: '12px',
                                            '&:hover': {
                                                borderColor: '#007bff'
                                            }
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 9999,
                                            position: 'absolute',
                                            width: '100%',
                                            minWidth: '100%',
                                            maxWidth: '100%'
                                        }),
                                        menuList: (provided) => ({
                                            ...provided,
                                            padding: '2px 0',
                                            maxHeight: '200px'
                                        }),
                                        option: (provided, state) => {
                                            const bg = selectOptionBackground(state);
                                            return {
                                                ...provided,
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                backgroundColor: bg,
                                                color: state.isSelected ? 'white' : '#495057',
                                                '&:hover': {
                                                    backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                }
                                            };
                                        },
                                        noOptionsMessage: (provided) => ({
                                            ...provided,
                                            padding: '8px 12px',
                                            fontSize: '12px',
                                            color: '#6c757d'
                                        }),
                                        menuPortal: (provided) => ({
                                            ...provided,
                                            zIndex: 9999
                                        })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Seller filter - only for non-Seller roles */}
                {!isSellerRole && (
                    <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                        <div className='row'>
                            <div className='col-12'>
                                <label className='labelHead' style={{
                                    fontWeight: '600',
                                    color: '#495057',
                                    marginBottom: '3px',
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }} htmlFor="sellerIdList">
                                    <i className="fa fa-user" style={{
                                        marginRight: '3px',
                                        color: '#6c757d',
                                        fontSize: '10px'
                                    }}></i> Seller
                                </label>
                            </div>
                            <div className='col-12 fontFix'>
                                <Select
                                    isMulti
                                    closeMenuOnSelect={false}
                                    options={sellerList}
                                    classNamePrefix="list"
                                    id='sellerIdList'
                                    name="sellerIdList"
                                    value={filterData?.sellerIdList}
                                    onChange={(value) =>
                                        handleMultiSelect(value, "sellerIdList")
                                    }
                                    styles={{
                                        control: (provided) => ({
                                            ...provided,
                                            borderRadius: '4px',
                                            border: '1px solid #ced4da',
                                            boxShadow: 'none',
                                            minHeight: '28px',
                                            fontSize: '12px',
                                            '&:hover': {
                                                borderColor: '#007bff'
                                            }
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            zIndex: 9999,
                                            position: 'absolute',
                                            width: '100%',
                                            minWidth: '100%',
                                            maxWidth: '100%'
                                        }),
                                        menuList: (provided) => ({
                                            ...provided,
                                            padding: '2px 0',
                                            maxHeight: '200px'
                                        }),
                                        option: (provided, state) => {
                                            const bg = selectOptionBackground(state);
                                            return {
                                                ...provided,
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                backgroundColor: bg,
                                                color: state.isSelected ? 'white' : '#495057',
                                                '&:hover': {
                                                    backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                }
                                            };
                                        },
                                        noOptionsMessage: (provided) => ({
                                            ...provided,
                                            padding: '8px 12px',
                                            fontSize: '12px',
                                            color: '#6c757d'
                                        }),
                                        menuPortal: (provided) => ({
                                            ...provided,
                                            zIndex: 9999
                                        })
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Carrier filter - for all roles */}
                <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                    <div className='row'>
                        <div className='col-12'>
                            <label className='labelHead' style={{
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '3px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                            }} htmlFor="carrierIdList">
                                <i className="fa fa-truck" style={{
                                    marginRight: '3px',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                }}></i> Carrier
                            </label>
                        </div>
                        <div className='col-12 fontFix'>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={carrierList}
                                classNamePrefix="list"
                                id='carrierIdList'
                                name="carrierIdList"
                                value={filterData?.carrierIdList}
                                onChange={(value) =>
                                    handleMultiSelect(value, "carrierIdList")
                                }
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        borderRadius: '4px',
                                        border: '1px solid #ced4da',
                                        boxShadow: 'none',
                                        minHeight: '28px',
                                        fontSize: '12px',
                                        '&:hover': {
                                            borderColor: '#007bff'
                                        }
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 9999,
                                        position: 'absolute',
                                        width: '100%',
                                        minWidth: '100%',
                                        maxWidth: '100%'
                                    }),
                                    menuList: (provided) => ({
                                        ...provided,
                                        padding: '2px 0',
                                        maxHeight: '200px'
                                    }),
                                        option: (provided, state) => {
                                            const bg = selectOptionBackground(state);
                                            return {
                                                ...provided,
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                backgroundColor: bg,
                                                color: state.isSelected ? 'white' : '#495057',
                                                '&:hover': {
                                                    backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                }
                                            };
                                        },
                                    noOptionsMessage: (provided) => ({
                                        ...provided,
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        color: '#6c757d'
                                    }),
                                    menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 9999
                                    })
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </div>
                </div>

                {/* Status filter - for all roles */}
                <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                    <div className='row'>
                        <div className='col-12'>
                            <label className='labelHead' style={{
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '3px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                            }} htmlFor="consignmentStatusIdList">
                                <i className="fa fa-info-circle" style={{
                                    marginRight: '3px',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                }}></i> Status
                            </label>
                        </div>
                        <div className='col-12 fontFix'>
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                options={statusList}
                                classNamePrefix="list"
                                id='consignmentStatusIdList'
                                name="consignmentStatusIdList"
                                value={filterData?.consignmentStatusIdList || []}
                                onChange={(value) =>
                                    handleMultiSelect(value, "consignmentStatusIdList")
                                }
                                styles={{
                                    control: (provided) => ({
                                        ...provided,
                                        borderRadius: '4px',
                                        border: '1px solid #ced4da',
                                        boxShadow: 'none',
                                        minHeight: '28px',
                                        fontSize: '12px',
                                        '&:hover': {
                                            borderColor: '#007bff'
                                        }
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 9999,
                                        position: 'absolute',
                                        width: '100%',
                                        minWidth: '100%',
                                        maxWidth: '100%'
                                    }),
                                    menuList: (provided) => ({
                                        ...provided,
                                        padding: '2px 0',
                                        maxHeight: '200px'
                                    }),
                                        option: (provided, state) => {
                                            const bg = selectOptionBackground(state);
                                            return {
                                                ...provided,
                                                padding: '8px 12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                backgroundColor: bg,
                                                color: state.isSelected ? 'white' : '#495057',
                                                '&:hover': {
                                                    backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa'
                                                }
                                            };
                                        },
                                    noOptionsMessage: (provided) => ({
                                        ...provided,
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        color: '#6c757d'
                                    }),
                                    menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 9999
                                    })
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </div>
                </div>

                {/* Created From Date - for all roles */}
                <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                    <div className='row'>
                        <div className='col-12'>
                            <label className='labelHead' style={{
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '3px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                            }} htmlFor="fromDate">
                                <i className="fa fa-calendar" style={{
                                    marginRight: '3px',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                }}></i> Created From Date
                            </label>
                        </div>
                        <div className='col-12 fontFix'>
                            <DatePickerComponent
                                dateField={filterData.fromDate}
                                handleDateChange={handleValidatedDateChange}
                                fieldName="fromDate"
                                dateFormat="MM-dd-yyyy"
                                id="fromDate"
                                showFutureDate={false}
                            />
                            {dateValidationErrors.fromDate && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.fromDate}
                                </div>
                            )}
                            {dateValidationErrors.fromDate_range && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.fromDate_range}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Created To Date - for all roles */}
                <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                    <div className='row'>
                        <div className='col-12'>
                            <label className='labelHead' style={{
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '3px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                            }} htmlFor="toDate">
                                <i className="fa fa-calendar" style={{
                                    marginRight: '3px',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                }}></i> Created To Date
                            </label>
                        </div>
                        <div className='col-12 fontFix'>
                            <DatePickerComponent
                                dateField={filterData.toDate}
                                handleDateChange={handleValidatedDateChange}
                                fieldName="toDate"
                                dateFormat="MM-dd-yyyy"
                                id="toDate"
                                showFutureDate={false}
                                minDate={filterData.fromDate ? new Date(filterData.fromDate) : null}
                            />
                            {dateValidationErrors.toDate && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.toDate}
                                </div>
                            )}
                            {dateValidationErrors.toDate_range && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.toDate_range}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                    <div className='row'>
                        <div className='col-12'>
                            <label className='labelHead' style={{
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '3px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                            }} htmlFor="modifiedFromDate">
                                <i className="fa fa-edit" style={{
                                    marginRight: '3px',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                }}></i> Modified From Date
                            </label>
                        </div>
                        <div className='col-12 fontFix'>
                            <DatePickerComponent
                                dateField={filterData.modifiedFromDate}
                                handleDateChange={handleValidatedDateChange}
                                fieldName="modifiedFromDate"
                                dateFormat="MM-dd-yyyy"
                                id="modifiedFromDate"
                                showFutureDate={false}
                            />
                            {dateValidationErrors.modifiedFromDate && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.modifiedFromDate}
                                </div>
                            )}
                            {dateValidationErrors.modifiedFromDate_range && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.modifiedFromDate_range}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Modified To Date - for all roles */}
                <div className='col-lg-2 col-md-4 col-sm-6 col-12' style={{ marginBottom: '8px', padding: '0 4px' }}>
                    <div className='row'>
                        <div className='col-12'>
                            <label className='labelHead' style={{
                                fontWeight: '600',
                                color: '#495057',
                                marginBottom: '3px',
                                fontSize: '12px',
                                display: 'flex',
                                alignItems: 'center'
                            }} htmlFor="modifiedToDate">
                                <i className="fa fa-edit" style={{
                                    marginRight: '3px',
                                    color: '#6c757d',
                                    fontSize: '10px'
                                }}></i> Modified To Date
                            </label>
                        </div>
                        <div className='col-12 fontFix'>
                            <DatePickerComponent
                                dateField={filterData.modifiedToDate}
                                handleDateChange={handleValidatedDateChange}
                                fieldName="modifiedToDate"
                                dateFormat="MM-dd-yyyy"
                                id="modifiedToDate"
                                showFutureDate={false}
                                minDate={filterData.modifiedFromDate ? new Date(filterData.modifiedFromDate) : null}
                            />
                            {dateValidationErrors.modifiedToDate && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.modifiedToDate}
                                </div>
                            )}
                            {dateValidationErrors.modifiedToDate_range && (
                                <div style={{ color: '#dc3545', fontSize: '11px', marginTop: '2px' }}>
                                    {dateValidationErrors.modifiedToDate_range}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Action Buttons - right aligned across the filter section */}
                <div className='col-12 mt-3'>
                    <div className='d-flex justify-content-end align-items-center' style={{ padding: '0 12px' }}>
                        <div className='d-flex gap-3 align-items-center'>
                            <button
                                className="btn cus-primary-transparent-btn clrBtn"
                                onClick={handleClearFilters}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid #6c757d'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#dc3545';
                                    e.target.style.color = '#dc3545';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#6c757d';
                                    e.target.style.color = '#6c757d';
                                }}
                            >
                                <i className="fa fa-eraser" style={{ marginRight: '6px', fontSize: '11px' }}></i> Clear Filter
                            </button>
                            <button
                                className="btn cus-seconday-bg-btn marginLeft5 searchBtn"
                                onClick={handleApplyFilter}
                                style={{
                                    padding: '8px 20px',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(0, 123, 255, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0, 123, 255, 0.2)';
                                }}
                            >
                                <i className="fa fa-check" style={{ marginRight: '6px', fontSize: '11px' }}></i> Apply
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

CarrierConnectFilter.propTypes = {
    showFilter: PropTypes.bool,
    handleReset: PropTypes.func,
    fetchCarrierConnectList: PropTypes.func,
    filterData: PropTypes.shape({
        carrierIdList: PropTypes.array,
        companyIdList: PropTypes.array,
        sellerIdList: PropTypes.array,
        consignmentStatusIdList: PropTypes.array,
        fromDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        toDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        modifiedFromDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        modifiedToDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        pageSize: PropTypes.number
    }),
    setfilterData: PropTypes.func,
    handleDateFieldChange: PropTypes.func
};

export default CarrierConnectFilter;
